import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Document, DocumentType } from 'common/models/Document';
import { EditDocument } from 'services/DocumentsService';
import RWTextFieldComponent from 'components/RWTextFieldComponent';
import DocumentTypePicker from 'components/Pickers/DocumentTypePicker';

interface Props {
  open: boolean;
  document: Document | null;
  entityTypeId: number;
  closeHandler: () => void;
  loadingHandler?: (isLoading: boolean) => void;
  errorHandler?: (message: string) => void;
  successHandler?: (
    message: string,
    fileId: number,
    fileName: string,
    fileType: number,
    expirationDate: string
  ) => void;
}

export default function EditDocumentDialog({
  open,
  document,
  entityTypeId,
  closeHandler,
  loadingHandler,
  errorHandler,
  successHandler,
}: Props) {
  const [editedDocument, setEditedDocument] = useState<Document | null>(null);

  useEffect(() => {
    setEditedDocument(document);
  }, [document]);

  const changeNameHandler = useCallback((name: string) => {
    setEditedDocument((prev) =>
      prev ? { ...prev, documentName: name + prev.documentExtension } : null
    );
  }, []);

  const changeDocTypeHandler = useCallback((v: DocumentType | null) => {
    const id = v?.id ?? 0;
    const tName = v?.name ?? '';
    setEditedDocument((prev) =>
      prev ? { ...prev, documentType: id, documentTypeName: tName } : null
    );
  }, []);

  const changeDateHandler = useCallback((date: moment.Moment | null) => {
    const dValue = date ? date.format('YYYY-MM-DD') : '0001-01-01';
    setEditedDocument((prev) => (prev ? { ...prev, expirationDate: dValue } : null));
  }, []);

  const changeNotesHandler = useCallback((notes: string) => {
    setEditedDocument((prev) => (prev ? { ...prev, notes: notes } : null));
  }, []);

  const docName = useMemo(() => {
    if (editedDocument) {
      const fullName = editedDocument.documentName;
      const ext = editedDocument.documentExtension;
      const index = fullName.lastIndexOf(ext);

      console.log(index);

      if (index !== -1) return fullName.substring(0, index);
      else return fullName;
    }
    return '';
  }, [editedDocument]);

  const expirationDate = useMemo(() => {
    if (editedDocument && editedDocument.expirationDate) {
      const m = moment(editedDocument.expirationDate);
      if (m.isValid() && m.year() > 1) return m;
    }
    return null;
  }, [editedDocument]);

  const editDocumentCallback = useCallback(async () => {
    if (editedDocument) {
      loadingHandler && loadingHandler(true);
      const res = await EditDocument(editedDocument.id, editedDocument, errorHandler);
      if (res) {
        successHandler &&
          successHandler(
            'Document Changed',
            editedDocument.id,
            editedDocument.documentName,
            editedDocument.documentType,
            editedDocument.expirationDate
          );
        closeHandler();
      }
      loadingHandler && loadingHandler(false);
    }
  }, [editedDocument, loadingHandler, errorHandler, successHandler, closeHandler]);

  const hasChanges = useMemo(() => {
    if (editedDocument && document) {
      if (editedDocument.documentName !== document.documentName) return true;
      if (editedDocument.documentType !== document.documentType) return true;
      if (editedDocument.expirationDate !== document.expirationDate) return true;
      if (editedDocument.notes !== document.notes) return true;
    }
    return false;
  }, [document, editedDocument]);

  return (
    <>
      <Dialog open={open} maxWidth="md" fullWidth>
        <DialogTitle>Edit Document</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <div />
            <RWTextFieldComponent
              label="Name"
              value={docName}
              onChange={(e) => changeNameHandler(e.target.value)}
            />
            <DocumentTypePicker
              value={editedDocument?.documentType ?? 0}
              entityTypeId={entityTypeId}
              changeHandler={changeDocTypeHandler}
            />
            <DatePicker
              label="Expiration Date"
              value={expirationDate}
              onChange={changeDateHandler}
              slotProps={{ actionBar: { actions: ['clear', 'today', 'cancel', 'accept'] } }}
            />
            <RWTextFieldComponent
              value={editedDocument ? editedDocument.notes : ''}
              label="Notes"
              multiline
              rows={5}
              onChange={({ target }) => changeNotesHandler(target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="error" onClick={closeHandler}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={!hasChanges || editedDocument === null || editedDocument.documentType === 0}
            onClick={editDocumentCallback}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
